/**
 * ObjectAbout — what an object is, as its module declares it, in a quiet
 * muted paragraph under a page heading. Text that runs past about two lines
 * shows its first sentence with a "More about {plural}" disclosure. Draws
 * nothing where the object declares no `about`. The words are the backend's;
 * nothing here rewrites them.
 */
import { useId, useState } from "react";
import { aboutTextOf } from "@shared-core/domain/object-ui/about";

export function ObjectAbout({
  about,
  plural,
}: {
  about: string | undefined | null;
  /** The object's plural, for the disclosure's label. */
  plural: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const textId = useId();
  const text = aboutTextOf(about);
  if (!text) return null;
  return (
    <div className="max-w-[70ch] text-sm text-muted-foreground">
      <div id={textId}>
        <AboutBody text={isOpen ? about!.trim() : text.lead} />
      </div>
      {text.isTruncated && (
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={textId}
          onClick={() => setIsOpen((open) => !open)}
          className="mt-1 text-primary hover:underline max-sm:py-2"
        >
          {isOpen ? "Less" : `More about ${plural.toLowerCase()}`}
        </button>
      )}
    </div>
  );
}

const STEP = /^\d+\.\s+/;

/** Lines starting "1. ", "2. " … read as a numbered list; other lines as paragraphs. */
function AboutBody({ text }: { text: string }) {
  const blocks: (string | string[])[] = [];
  for (const line of text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)) {
    const last = blocks[blocks.length - 1];
    if (STEP.test(line)) {
      if (Array.isArray(last)) last.push(line.replace(STEP, ""));
      else blocks.push([line.replace(STEP, "")]);
    } else blocks.push(line);
  }
  return (
    <>
      {blocks.map((block, i) =>
        Array.isArray(block) ? (
          <ol
            key={i}
            className="mt-1 list-decimal space-y-0.5 pl-5 marker:font-semibold marker:text-foreground"
          >
            {block.map((step, j) => (
              <li key={`${i}-${j}`}>{step}</li>
            ))}
          </ol>
        ) : (
          <p key={i}>{block}</p>
        ),
      )}
    </>
  );
}
